<template>
  <div class="rm-screen">
    <div v-if="!selectedCompany" class="rm-empty">
      <v-icon size="40" color="#8a817a">mdi-domain</v-icon>
      {{ $t("report_mobile.employees_pick_company") }}
    </div>
    <template v-else>
      <label class="rm-switch-row">
        {{ $t("more_restaurants_report.whole_company") }}
        <input v-model="includeBranches" type="checkbox" role="switch" />
      </label>

      <div v-if="loading" class="rm-empty">
        <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
      </div>
      <div v-else-if="!workers.length" class="rm-empty">
        <v-icon v-if="!filters.search" size="40" color="#8a817a">mdi-account-off-outline</v-icon>
        {{ emptyTitle }}
      </div>

      <template v-else>
        <div class="rm-stats rm-stats--pair">
          <div class="rm-stat rm-stat--inverse">
            <span class="rm-stat__label">{{ $t("report_desktop.total_meals") }}</span>
            <span class="rm-stat__value rm-num">{{ sums.quantity }}</span>
          </div>
          <div class="rm-stat">
            <span class="rm-stat__label">{{ $t("taken") }}</span>
            <span class="rm-stat__value rm-num">{{ sums.quantity_payed }}</span>
          </div>
        </div>

        <section v-for="worker in workers" :key="worker.key" class="rm-card mi-worker">
          <header class="mi-worker__head">
            <span class="mi-worker__avatar" aria-hidden="true">{{ worker.initials }}</span>
            <span class="mi-worker__name">{{ worker.name }}</span>
            <span class="mi-worker__total rm-num">{{ worker.quantity }}</span>
          </header>
          <ul class="rm-list">
            <li v-for="row in worker.rows" :key="row.rowKey" class="mi-row">
              <span class="mi-row__main">
                <span class="mi-row__item">{{ row.quantity }}× {{ row.itemText }}</span>
                <span v-if="row.comboText" class="mi-row__combo rm-muted">{{ row.comboText }}</span>
                <span class="mi-row__meta rm-muted rm-num">
                  {{ row.dateText }}
                  <report-shift-tag v-if="row.shiftNumber" :shift="row.shiftNumber"></report-shift-tag>
                  <template v-if="showAmount && row.amount">· {{ row.amount }}</template>
                </span>
              </span>
              <span v-if="takenState(row)" class="rm-status" :class="`rm-status--${takenState(row)}`">
                {{ $t("taken") }} {{ row.quantity_payed }}
              </span>
            </li>
          </ul>
        </section>
      </template>
    </template>
  </div>
</template>

<script>
import ReportMealsTabEmployeeItems from "@/components/report/more-restaurants/tabs/ReportMealsTabEmployeeItems.vue";
import hasShiftBinding from "../../../shared/hasShiftBinding";
import hasShiftSubtitle from "../../../shared/hasShiftSubtitle";
import hasMoreWorkerItemRows from "../../../shared/more/hasMoreWorkerItemRows";
import ReportShiftTag from "../../../shared/ReportShiftTag.vue";

/**
 * Mobilni "Radnici – po stavkama" (firme koje hrani restoran): za svakog radnika šta je naručio po danu i smeni
 * (paket / obrok / jelo, količina, jela u paketu, preuzeto). "Cela firma" (sa filijalama) je prekidač na vrhu.
 * Firma, smene i period su u zaglavlju, pretraga u lupi. Nasleđuje učitavanje, štampu i Excel iz starog taba.
 */
export default {
  name: "MoreMobileWorkerItems",
  extends: ReportMealsTabEmployeeItems,
  mixins: [hasShiftBinding, hasShiftSubtitle, hasMoreWorkerItemRows],
  components: { ReportShiftTag },
  computed: {
    /** Redovi po radniku (redovi jednog radnika stižu uzastopno). */
    workers() {
      const workers = [];
      this.shownRows.forEach((row) => {
        if (row.showWorker) {
          workers.push({ key: `${row.worker}-${workers.length}`, name: row.worker, initials: row.initials, quantity: 0, rows: [] });
        }
        const worker = workers[workers.length - 1];
        worker.rows.push(row);
        worker.quantity += Number(row.quantity) || 0;
      });
      return workers;
    },
  },
  methods: {
    /** Preuzeto: sve (zeleno) ili deo (žuto); ništa se ne ističe. */
    takenState(row) {
      const taken = Number(row.quantity_payed) || 0;
      if (!taken) {
        return "";
      }
      return taken >= (Number(row.quantity) || 0) ? "all" : "part";
    },
  },
};
</script>

<style lang="scss">
.rm-theme .mi-worker {
  &__head {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 52px;
    padding: 8px 14px;
    border-bottom: 1px solid var(--rm-divider-soft);
  }

  &__avatar {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--rm-accent-soft);
    color: var(--rm-accent-strong);
    font-size: 12px;
    font-weight: 700;
  }

  &__name {
    flex: 1;
    min-width: 0;
    font-size: 15px;
    font-weight: 700;
    overflow-wrap: anywhere;
  }

  &__total {
    font-size: 18px;
    font-weight: 700;
  }
}

.rm-theme .mi-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 56px;
  padding: 8px 14px;
  box-sizing: border-box;

  &:not(:last-child) {
    border-bottom: 1px solid var(--rm-divider-soft);
  }

  &__main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__item {
    font-size: 15px;
    font-weight: 600;
    overflow-wrap: anywhere;
  }

  &__combo {
    font-size: 13px;
    overflow-wrap: anywhere;
  }

  &__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 12px;
  }
}
</style>
